import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import unsplash from "../api/unsplash";
import GlobalSnackbar from "./Snackbar";
import LinearProgress from "@mui/material/LinearProgress";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import SecurityIcon from "@mui/icons-material/Security";
import GavelIcon from "@mui/icons-material/Gavel";
import DescriptionIcon from "@mui/icons-material/Description";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import {
  DataGridPro,
  itIT,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from "../utils/PersistedDataGridPro";
import { CustomFooterPersonalized } from "../utils/CustomFooterPersonalized";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`rischi-tabpanel-${index}`}
      aria-labelledby={`rischi-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function CustomToolbar() {
  return (
    <GridToolbarContainer>
      <Grid
        container
        direction="row"
        justifyContent="flex-start"
        alignItems="center"
      >
        <GridToolbarQuickFilter placeholder="Cerca..." />
      </Grid>
    </GridToolbarContainer>
  );
}

function DettaglioRigaDialog({ open, onClose, onSave, row, title, rischi }) {
  const [form, setForm] = useState({
    ID: null,
    ID_Rischi_Sicurezza: null,
    Descrizione: "",
  });

  useEffect(() => {
    if (row) {
      setForm({
        ID: row.ID || null,
        ID_Rischi_Sicurezza: row.ID_Rischi_Sicurezza || null,
        Descrizione: row.Descrizione || "",
      });
    } else {
      setForm({
        ID: null,
        ID_Rischi_Sicurezza: null,
        Descrizione: "",
      });
    }
  }, [row, open]);

  const rischioSel = useMemo(
    () => rischi.find((r) => r.ID === form.ID_Rischi_Sicurezza) || null,
    [rischi, form.ID_Rischi_Sicurezza],
  );

  const handleSave = () => {
    onSave(form);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <Autocomplete
              options={rischi}
              value={rischioSel}
              onChange={(e, value) =>
                setForm({ ...form, ID_Rischi_Sicurezza: value?.ID || null })
              }
              getOptionLabel={(option) => option?.Nome || ""}
              renderInput={(params) => (
                <TextField {...params} label="Rischio" fullWidth />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Descrizione"
              value={form.Descrizione}
              onChange={(e) =>
                setForm({ ...form, Descrizione: e.target.value })
              }
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
        <Button
          variant="contained"
          className="ButtonMiller"
          onClick={onClose}
          startIcon={<CloseIcon />}
        >
          Chiudi
        </Button>
        <Button
          variant="contained"
          className="ButtonMiller"
          onClick={handleSave}
          startIcon={<CheckIcon />}
        >
          Salva
        </Button>
      </DialogActions>
    </Dialog>
  );
}

DettaglioRigaDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  row: PropTypes.object,
  title: PropTypes.string.isRequired,
  rischi: PropTypes.array.isRequired,
};

function DeleteDialog({ open, onClose, onConfirm, descrizione }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Elimina voce</DialogTitle>
      <DialogContent>
        <Typography>Confermi l&apos;eliminazione?</Typography>
        <Typography sx={{ mt: 1, fontWeight: 600 }}>{descrizione}</Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={onClose}>
          Annulla
        </Button>
        <Button color="error" variant="contained" onClick={onConfirm}>
          Elimina
        </Button>
      </DialogActions>
    </Dialog>
  );
}

DeleteDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  descrizione: PropTypes.string,
};

function DescrizioneRischioDialog({ open, onClose, onSave, row }) {
  const [form, setForm] = useState({
    ID: null,
    NomeRischio: "",
    Descrizione: "",
  });

  useEffect(() => {
    if (row) {
      setForm({
        ID: row.ID || null,
        NomeRischio: row.NomeRischio || "",
        Descrizione: row.Descrizione || "",
      });
    } else {
      setForm({
        ID: null,
        NomeRischio: "",
        Descrizione: "",
      });
    }
  }, [row, open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Modifica descrizione rischio</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Rischio"
              value={form.NomeRischio}
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={4}
              label="Descrizione"
              value={form.Descrizione}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, Descrizione: e.target.value }))
              }
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
        <Button
          variant="contained"
          className="ButtonMiller"
          onClick={onClose}
          startIcon={<CloseIcon />}
        >
          Chiudi
        </Button>
        <Button
          variant="contained"
          className="ButtonMiller"
          onClick={() => onSave(form)}
          startIcon={<CheckIcon />}
        >
          Salva
        </Button>
      </DialogActions>
    </Dialog>
  );
}

DescrizioneRischioDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  row: PropTypes.object,
};

function TabAnagrafica({ tipo, titolo }) {
  const [loading, setLoading] = useState(true);
  const [loadingRischi, setLoadingRischi] = useState(true);
  const [rows, setRows] = useState([]);
  const [rischi, setRischi] = useState([]);
  const [rischioFilter, setRischioFilter] = useState(null);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openSnack, setOpenSnack] = useState(false);
  const [snack, setSnack] = useState({
    severity: "success",
    messaggio: "Operazione completata",
  });

  const closeSnack = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnack(false);
  };

  const authHeaders = {
    headers: {
      Authorization: `Bearer ${localStorage.getItem("jwt")}`,
    },
  };

  const loadRischi = async () => {
    setLoadingRischi(true);
    try {
      const response = await unsplash.get("rischiSicurezza", authHeaders);
      setRischi(response.data.data || []);
    } finally {
      setLoadingRischi(false);
    }
  };

  const loadRows = async (idRischio) => {
    setLoading(true);
    try {
      const response = await unsplash.get("dvr/anagraficaRischi", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("jwt")}`,
        },
        params: {
          tipo,
          idRischio: idRischio || undefined,
        },
      });
      setRows(response.data.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRischi();
    loadRows();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadRows(rischioFilter?.ID);
  }, [rischioFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const notify = (severity, messaggio) => {
    setSnack({ severity, messaggio });
    setOpenSnack(true);
  };

  const handleSave = async (item) => {
    try {
      if (!item?.ID_Rischi_Sicurezza || !item?.Descrizione?.trim()) {
        notify("warning", "Compila rischio e descrizione");
        return;
      }

      if (item?.ID) {
        await unsplash.put("dvr/anagraficaRischi", { tipo, item }, authHeaders);
      } else {
        await unsplash.post(
          "dvr/anagraficaRischi",
          { tipo, item },
          authHeaders,
        );
      }

      notify("success", "Salvataggio effettuato");
      setOpenEdit(false);
      setSelectedRow(null);
      loadRows(rischioFilter?.ID);
    } catch {
      notify("error", "Errore durante il salvataggio");
    }
  };

  const handleDelete = async () => {
    if (!selectedRow?.ID) {
      return;
    }
    try {
      await unsplash.delete("dvr/anagraficaRischi", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("jwt")}`,
        },
        data: {
          tipo,
          ID: selectedRow.ID,
        },
      });
      notify("success", "Eliminazione effettuata");
      setOpenDelete(false);
      setSelectedRow(null);
      loadRows(rischioFilter?.ID);
    } catch {
      notify("error", "Errore durante l'eliminazione");
    }
  };

  const columns = [
    {
      field: "Actions",
      headerName: "Azioni",
      width: 120,
      sortable: false,
      filterable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <>
          <IconButton
            onClick={() => {
              setSelectedRow(params.row);
              setOpenEdit(true);
            }}
          >
            <EditIcon sx={{ color: "#2265B1" }} />
          </IconButton>
          <IconButton
            onClick={() => {
              setSelectedRow(params.row);
              setOpenDelete(true);
            }}
          >
            <DeleteIcon sx={{ color: "#FF2768" }} />
          </IconButton>
        </>
      ),
    },
    {
      field: "NomeRischio",
      headerName: "Rischio",
      flex: 0.5,
      minWidth: 250,
      align: "left",
      headerAlign: "center",
    },
    {
      field: "Descrizione",
      headerName: "Descrizione",
      flex: 1,
      minWidth: 400,
      align: "left",
      headerAlign: "center",
    },
  ];

  return (
    <>
      <GlobalSnackbar
        severity={snack.severity}
        messaggio={snack.messaggio}
        openSnack={openSnack}
        handleCloseSnack={closeSnack}
      />
      <Grid
        container
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Grid item xs={12} md={8}>
          <Autocomplete
            options={rischi}
            loading={loadingRischi}
            value={rischioFilter}
            onChange={(e, value) => setRischioFilter(value)}
            getOptionLabel={(option) => option?.Nome || ""}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Filtra per rischio (opzionale)"
                fullWidth
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md="auto">
          <Button
            variant="contained"
            className="ButtonMiller"
            startIcon={<AddIcon />}
            onClick={() => {
              setSelectedRow({
                ID_Rischi_Sicurezza: rischioFilter?.ID || null,
                Descrizione: "",
              });
              setOpenEdit(true);
            }}
          >
            Aggiungi
          </Button>
        </Grid>
      </Grid>

      <Typography sx={{ mb: 1, fontWeight: 600 }}>{titolo}</Typography>
      <Box sx={{ height: { xs: "70vh", md: "58vh", xl: "70vh" }, width: 1 }}>
        <DataGridPro
          initialState={{
            pinnedColumns: { left: ["Actions"] },
          }}
          components={{
            Toolbar: CustomToolbar,
            Footer: CustomFooterPersonalized,
          }}
          slots={{
            loadingOverlay: LinearProgress,
          }}
          loading={loading}
          localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
          rows={rows}
          columns={columns}
          getRowId={(row) => row.ID}
          pageSize={20}
          rowsPerPageOptions={[20]}
          rowHeight={52}
          disableSelectionOnClick
        />
      </Box>

      <DettaglioRigaDialog
        open={openEdit}
        onClose={() => {
          setOpenEdit(false);
          setSelectedRow(null);
        }}
        onSave={handleSave}
        row={selectedRow}
        title={selectedRow?.ID ? "Modifica voce" : "Nuova voce"}
        rischi={rischi}
      />

      <DeleteDialog
        open={openDelete}
        onClose={() => {
          setOpenDelete(false);
          setSelectedRow(null);
        }}
        onConfirm={handleDelete}
        descrizione={selectedRow?.Descrizione}
      />
    </>
  );
}

TabAnagrafica.propTypes = {
  tipo: PropTypes.oneOf(["governo", "misure"]).isRequired,
  titolo: PropTypes.string.isRequired,
};

function TabDescrizioneRischio() {
  const [loading, setLoading] = useState(true);
  const [loadingRischi, setLoadingRischi] = useState(true);
  const [rows, setRows] = useState([]);
  const [rischi, setRischi] = useState([]);
  const [rischioFilter, setRischioFilter] = useState(null);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openSnack, setOpenSnack] = useState(false);
  const [snack, setSnack] = useState({
    severity: "success",
    messaggio: "Operazione completata",
  });

  const authHeaders = {
    headers: {
      Authorization: `Bearer ${localStorage.getItem("jwt")}`,
    },
  };

  const notify = (severity, messaggio) => {
    setSnack({ severity, messaggio });
    setOpenSnack(true);
  };

  const closeSnack = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnack(false);
  };

  const loadRischi = async () => {
    setLoadingRischi(true);
    try {
      const response = await unsplash.get("rischiSicurezza", authHeaders);
      setRischi(response.data.data || []);
    } finally {
      setLoadingRischi(false);
    }
  };

  const loadRows = async (idRischio) => {
    setLoading(true);
    try {
      const response = await unsplash.get("dvr/anagraficaRischi", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("jwt")}`,
        },
        params: {
          tipo: "descrizione",
          idRischio: idRischio || undefined,
        },
      });
      setRows(response.data.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRischi();
    loadRows();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadRows(rischioFilter?.ID);
  }, [rischioFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async (item) => {
    try {
      if (!item?.ID || !item?.Descrizione?.trim()) {
        notify("warning", "Compila la descrizione del rischio");
        return;
      }

      await unsplash.put(
        "dvr/anagraficaRischi",
        { tipo: "descrizione", item },
        authHeaders,
      );

      notify("success", "Salvataggio effettuato");
      setOpenEdit(false);
      setSelectedRow(null);
      loadRows(rischioFilter?.ID);
    } catch {
      notify("error", "Errore durante il salvataggio");
    }
  };

  const handleDelete = async () => {
    if (!selectedRow?.ID) {
      return;
    }
    try {
      await unsplash.delete("dvr/anagraficaRischi", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("jwt")}`,
        },
        data: {
          tipo: "descrizione",
          ID: selectedRow.ID,
        },
      });
      notify("success", "Eliminazione effettuata");
      setOpenDelete(false);
      setSelectedRow(null);
      loadRows(rischioFilter?.ID);
    } catch {
      notify("error", "Errore durante l'eliminazione");
    }
  };

  const columns = [
    {
      field: "Actions",
      headerName: "Azioni",
      width: 120,
      sortable: false,
      filterable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <>
          <IconButton
            onClick={() => {
              setSelectedRow(params.row);
              setOpenEdit(true);
            }}
          >
            <EditIcon sx={{ color: "#2265B1" }} />
          </IconButton>
          <IconButton
            onClick={() => {
              setSelectedRow(params.row);
              setOpenDelete(true);
            }}
          >
            <DeleteIcon sx={{ color: "#FF2768" }} />
          </IconButton>
        </>
      ),
    },
    {
      field: "NomeRischio",
      headerName: "Rischio",
      flex: 0.5,
      minWidth: 260,
      align: "left",
      headerAlign: "center",
    },
    {
      field: "Descrizione",
      headerName: "Descrizione",
      flex: 1,
      minWidth: 450,
      align: "left",
      headerAlign: "center",
    },
  ];

  return (
    <>
      <GlobalSnackbar
        severity={snack.severity}
        messaggio={snack.messaggio}
        openSnack={openSnack}
        handleCloseSnack={closeSnack}
      />
      <Grid
        container
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Grid item xs={12} md={8}>
          <Autocomplete
            options={rischi}
            loading={loadingRischi}
            value={rischioFilter}
            onChange={(e, value) => setRischioFilter(value)}
            getOptionLabel={(option) => option?.Nome || ""}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Filtra per rischio (opzionale)"
                fullWidth
              />
            )}
          />
        </Grid>
      </Grid>
      <Typography sx={{ mb: 1, fontWeight: 600 }}>
        Descrizione del rischio (testo base nel DVR)
      </Typography>
      <Box sx={{ height: { xs: "70vh", md: "58vh", xl: "70vh" }, width: 1 }}>
        <DataGridPro
          initialState={{
            pinnedColumns: { left: ["Actions"] },
          }}
          components={{
            Toolbar: CustomToolbar,
            Footer: CustomFooterPersonalized,
          }}
          slots={{
            loadingOverlay: LinearProgress,
          }}
          loading={loading}
          localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
          rows={rows}
          columns={columns}
          getRowId={(row) => row.ID}
          pageSize={20}
          rowsPerPageOptions={[20]}
          rowHeight={52}
          disableSelectionOnClick
        />
      </Box>
      <DescrizioneRischioDialog
        open={openEdit}
        onClose={() => {
          setOpenEdit(false);
          setSelectedRow(null);
        }}
        onSave={handleSave}
        row={selectedRow}
      />
      <DeleteDialog
        open={openDelete}
        onClose={() => {
          setOpenDelete(false);
          setSelectedRow(null);
        }}
        onConfirm={handleDelete}
        descrizione={selectedRow?.NomeRischio}
      />
    </>
  );
}

export const Rischi = () => {
  const [value, setValue] = useState(0);

  const handleChangeTab = (event, newValue) => {
    setValue(newValue);
  };

  return (
    <Grid item xs={12}>
      <Card>
        <CardContent sx={{ p: { xs: 0 } }}>
          <Card>
            <CardContent
              style={{
                paddingLeft: "32px",
                paddingRight: "32px",
                paddingTop: 0,
              }}
            >
              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs
                  value={value}
                  textColor="inherit"
                  variant="scrollable"
                  onChange={handleChangeTab}
                  aria-label="tabs rischi"
                >
                  <Tab
                    icon={<SecurityIcon />}
                    iconPosition="start"
                    label="Governo del rischio"
                  />
                  <Tab
                    icon={<GavelIcon />}
                    iconPosition="start"
                    label="Misure cogenti da applicare nell'immediato"
                  />
                  <Tab
                    icon={<DescriptionIcon />}
                    iconPosition="start"
                    label="Descrizione del rischio"
                  />
                </Tabs>
              </Box>
              <TabPanel value={value} index={0}>
                <TabAnagrafica tipo="governo" titolo="Governo del rischio" />
              </TabPanel>
              <TabPanel value={value} index={1}>
                <TabAnagrafica
                  tipo="misure"
                  titolo="Misure cogenti da applicare nell'immediato"
                />
              </TabPanel>
              <TabPanel value={value} index={2}>
                <TabDescrizioneRischio />
              </TabPanel>
            </CardContent>
          </Card>
        </CardContent>
      </Card>
    </Grid>
  );
};
